import React, { FunctionComponent, useState } from 'react';
import { useForm } from 'react-hook-form';
import AuthCode from 'react-auth-code-input';
import { MfaStatusType } from '@upstart/harmony/lib/legos/constants/user';
import logo from '../../assets/upstart-white.png';
import { verifyOTP, resendOTP, login } from '../../redux/auth/thunks';
import { useAppDispatch, useTypedSelector } from '../../redux/store';
import { Button } from '../Input/Button/Button';
import styles from '../Login/styles.module.scss';
import { LoginError } from '../Login/LoginError';
import { Checkbox } from '../Input/Checkbox/Checkbox';
import { EXPIRED_SKIP_MFA_DATE } from '../../utils/constants';
import { LockedOutModal } from '../Login/LockedOutModal';

interface FormData {
  rememberDevice: boolean;
}

interface Props {
  mfaLoginToken: string;
}

const isBeforeMfaDeadline =
  new Date().getTime() < new Date(EXPIRED_SKIP_MFA_DATE).getTime();

function getSkipMfaMessage() {
  const msg = 'Having trouble getting authenticated?';
  if (isBeforeMfaDeadline) {
    return `${msg} You can skip authentication until ${EXPIRED_SKIP_MFA_DATE}.`;
  }

  return msg;
}

export const OTPForm: FunctionComponent<Props> = ({ mfaLoginToken }) => {
  const form = useForm<FormData>({ mode: 'onTouched' });
  const dispatch = useAppDispatch();
  const [otp, setOtp] = useState('');
  const authStore = useTypedSelector((state) => state.auth);
  const [sendCodeBtnLoading, setSendCodeBtnLoading] = useState(false);

  const showSkipMFA =
    authStore.legacyLoginDisabled === false || isBeforeMfaDeadline;
  const showLockedOutModal =
    authStore.error.status === MfaStatusType.ACCOUNT_LOCKED;

  const errorMsg = authStore.error.message ? authStore.error.message : '';

  const onSubmit = async (data: FormData) => {
    const params = {
      ...data,
      otp,
      mfaLoginToken,
    };
    await dispatch(verifyOTP(params));
  };

  const onSendCodeAgain = async () => {
    const params = {
      mfaLoginToken,
    };
    setSendCodeBtnLoading(true);
    await dispatch(resendOTP(params));
    setSendCodeBtnLoading(false);
  };

  const onSkipMFA = async () => {
    await dispatch(
      login({
        email: authStore.credentials.email,
        password: authStore.credentials.password,
      }),
    );
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.header}>
        <img src={logo} alt="Logo" />
        Launch Pad
      </div>
      {!showLockedOutModal && (
        <form
          className={styles.loginContainer}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className={styles.loginTitle}>Authenticate Your Account</div>
          <div className={styles.loginSubtitle}>
            Enter the 6-digit authentication code from the email or text message
            we just sent you.
          </div>
          <LoginError error={errorMsg} />
          <AuthCode
            allowedCharacters="numeric"
            containerClassName={styles.otpInputs}
            onChange={(otpCode) => setOtp(otpCode)}
          />
          <Checkbox
            form={form}
            name="rememberDevice"
            className={styles.rememberCheckbox}
            label="Remember this device for 30 days"
          />
          <Button
            className={styles.submitButton}
            label="Submit"
            type="submit"
            variant="primary"
            loading={form.formState.isSubmitting}
            disabled={!/^\d{6}$/.test(otp)}
          />
          <Button
            className={styles.sendCodeAgainButton}
            label="Send Code Again"
            type="button"
            variant="default"
            loading={sendCodeBtnLoading}
            onClick={onSendCodeAgain}
          />
          {showSkipMFA && (
            <div className={styles.otpFormSkipSection}>
              <p className={styles.otpFormMessage}>{getSkipMfaMessage()}</p>
              <button
                className={styles.skipAuthenticationBtn}
                type="button"
                onClick={onSkipMFA}
              >
                SKIP AUTHENTICATION
              </button>
            </div>
          )}
          <p className={styles.otpFormMessage}>
            It may take up to five minutes to get the code. Please check your
            spam folder too. If you&apos;re still having problems logging in,{' '}
            <b>please contact our support team at 800-535-9749</b> Monday-Sunday
            9am-11pm EST.
          </p>
        </form>
      )}
      {showLockedOutModal && <LockedOutModal lockedOutMsg={errorMsg} />}
      <div className="spacer" />
    </div>
  );
};
